import { Component, signal } from '@angular/core';
import {
	Activity,
	ContentScoringMethod,
	ReportingStudentReport,
	ReportingStudentTake,
	ReportingStudentTakeReport,
	Take,
	TakeStatus,
} from '@mira-avant/mtlibs';
import { NavigationService } from '../../../../../services/navigation.service';
import { SectionService } from '../../../../../services/section.service';
import { AngularModule } from '../../../../../modules/angular.module';
import { ZorroModule } from '../../../../../modules/zorro.module';
import { CoachChat } from '../../../../../services/coach.service';
import { CoachChatComponent } from '../../coach-chat/coach-chat.component';
import { JourneyReports } from '../../../../../services/journey-reports';

@Component({
	selector: 'app-journey-student-report',
	standalone: true,
	imports: [AngularModule, ZorroModule, CoachChatComponent],
	templateUrl: './journey-student-report.html',
	styleUrl: './journey-student-report.scss',
})
export class JourneyStudentReport {
	report = signal<ReportingStudentReport | undefined>(undefined);
	selectedLessonUuid = signal<string | undefined>(undefined);
	selectedActivity = signal<Activity | undefined>(undefined);
	selectedActivityReport = signal<ReportingStudentTakeReport | undefined>(undefined);
	selectedCoachChat = signal<CoachChat | undefined>(undefined);
	selectedTakeNumber = signal<number | undefined>(undefined);
	studentUuid = signal<string | undefined>(undefined);
	takeReport = signal<ReportingStudentTake | undefined>(undefined);

	public TakeStatus = TakeStatus;
	public ContentScoringMethod = ContentScoringMethod;

	constructor(
		public nav: NavigationService,
		private journeyReportsService: JourneyReports,
		public sectionService: SectionService
	) {
		this.ngOnInit();
	}

	async ngOnInit(): Promise<void> {
		const matrixParams = await this.nav.matrixParams$.getValue();

		const sectionUuid: string = matrixParams['sectionUuid'];
		const studentUuid: string = matrixParams['studentUuid'];

		const startOfYear: Date = new Date(new Date().getFullYear(), 0, 1);
		const now: Date = new Date();

		const report = await this.journeyReportsService.getStudentReport$(studentUuid, sectionUuid, startOfYear, now);

		this.report.set(report);
	}

	private clearSelectedActivityReport(): void {
		this.selectedActivityReport.set(undefined);
		this.selectedCoachChat.set(undefined);
		this.selectedLessonUuid.set(undefined);
		this.selectedActivity.set(undefined);
	}

	public async toggleSelectedActivityReport(
		activity: Activity,
		lessonUuid: string,
		activityUuid: string
	): Promise<void> {
		if (this.selectedActivityReport() && this.selectedActivityReport()!.activityUuid === activityUuid) {
			this.clearSelectedActivityReport();
		} else {
			this.clearSelectedActivityReport();

			this.selectedLessonUuid.set(lessonUuid);
			this.selectedActivity.set(activity);

			await this.selectActivityReport(activityUuid);
		}
	}

	public async onExpandedTakeChange(expanded: boolean, take: Take): Promise<void> {
		this.selectedCoachChat.set(undefined);

		if (expanded) {
			const matrixParams = await this.nav.matrixParams$.getValue();
			const studentUuid: string = matrixParams['studentUuid'];

			this.studentUuid.set(studentUuid);
			this.selectedTakeNumber.set(take.take);

			if (!this.selectedActivityReport()) {
				throw new Error('No activity report found');
			}

			this.selectedActivityReport()!.report.every(async (takeReport: ReportingStudentTake) => {
				if (!studentUuid) {
					throw new Error('No student UUID found');
				}

				if (!this.selectedTakeNumber()) {
					throw new Error('No take number found');
				}

				this.takeReport.set(takeReport);

				// commented coach chat logic remains unchanged
			});
		} else {
			this.selectedTakeNumber.set(undefined);
		}
	}

	public async selectActivityReport(activityUuid: string): Promise<void> {
		const matrixParams = await this.nav.matrixParams$.getValue();

		const sectionUuid: string = matrixParams['sectionUuid'];
		const studentUuid: string = matrixParams['studentUuid'];

		const report = await this.journeyReportsService.getContentTakes$(activityUuid, sectionUuid, studentUuid);

		this.selectedActivityReport.set(report);
	}
}
